<template>
  <validation-observer ref="observer" v-slot="{ passes }" slim>
    <b-modal
      :title="title || dialogTitle"
      v-model="dialog"
      :hide-header-close="saving"
      @ok.prevent="passes(save)"
      @close.prevent="close"
      @cancel.prevent="close"
      :size="modalSize"
      :no-close-on-esc="saving"
      :no-close-on-backdrop="saving"
      :scrollable="scrollable"
    >
      <b-container fluid>
        <base-dialog-alert />
        <b-form @submit.stop.prevent="passes(save)">
          <slot
            name="form"
            v-bind:editedItem="editedItem"
            v-bind:isEditMode="isEditMode"
          >
            <b-row>
              <b-col cols="12">
                <base-input
                  v-if="!noNameProp"
                  :label="$t('common.name')"
                  v-model="editedItem.name"
                  validatable
                  next-tick
                />
                <slot
                  name="formFields"
                  v-bind:editedItem="editedItem"
                  v-bind:isEditMode="isEditMode"
                ></slot>
              </b-col>
            </b-row>
          </slot>
        </b-form>
      </b-container>
      <template #modal-footer="{ cancel, ok }">
        <b-button
          variant="secondary"
          :disabled="saving"
          @click.prevent="cancel"
        >
          {{ $t("common.close") }}
        </b-button>
        <base-btn
          variant="primary"
          :loading="saving"
          @click="ok"
          :disabled="disableSubmit"
        >
          {{
            $te(`${localizationSectionName}.${isEditMode ? "save" : "add"}`)
              ? $t(`${localizationSectionName}.${isEditMode ? "save" : "add"}`)
              : $t(`common.${isEditMode ? "save" : "add"}`)
          }}
        </base-btn>
      </template>
    </b-modal>
  </validation-observer>
</template>

<script>
import {
  dialogMixin,
  dialogHelpers,
  crudCommonMixin,
} from "@foys-tech/foys-utils";

import {
  BaseBtn,
  BaseInput,
  BaseDialogAlert,
} from "@foys-tech/foys-base-components";

import { getErrorMessage } from "@foys-tech/foys-utils/dialogMixin";
export default {
  mixins: [crudCommonMixin, dialogMixin, dialogHelpers],
  components: { BaseBtn, BaseInput, BaseDialogAlert },
  props: {
    isEditOnly: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: null,
    },
    modalSize: {
      type: String,
      default: "md",
    },
    scrollable: {
      type: Boolean,
      default: false,
    },
    disableSubmit: {
      type: Boolean,
      default: false,
    },
    requestParamsFn: {
      type: Function,
    },
    editModelFn: {
      type: Function,
    },
    saveModelFn: {
      type: Function,
    },
    loadReferencesFn: {
      type: Function,
    },
    addAction: {
      type: String,
    },
    updateAction: {
      type: String,
    },
    onSubmitAction: {
      type: Function,
    },
    skipAddAction: {
      type: Boolean,
      default: false,
    },
    skipUpdateAction: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editOnly: false,
    };
  },
  computed: {
    saving() {
      return this.$store.getters[`${this.ns}${this.entityName}Saving`];
    },
    saveError() {
      return this.$store.getters[`${this.ns}${this.entityName}Error`];
    },
    dialogTitle() {
      if (this.isEditMode) {
        if (this.$te(`${this.localizationSectionName}.edit_title`)) {
          return this.$t(`${this.localizationSectionName}.edit_title`);
        }

        if (this.$te(`${this.localizationSectionName}.title`)) {
          return `${this.$t("common.edit")} ${this.$t(
            `${this.localizationSectionName}.title`
          ).toLowerCase()}`;
        }
        return `${this.$t("common.edit")} ${this.entityNameInContext}`;
      }

      if (this.$te(`${this.localizationSectionName}.add_title`)) {
        return this.$t(`${this.localizationSectionName}.add_title`);
      }

      if (this.$te(`${this.localizationSectionName}.title`)) {
        return `${this.$t("common.add")} ${this.$t(
          `${this.localizationSectionName}.title`
        ).toLowerCase()}`;
      }
      return `${this.$t("common.add")} ${this.entityNameInContext}`;
    },
  },
  methods: {
    triggerEventAction(item) {
      this.editOnly = this.isEditOnly;
      let editModel = Object.assign({}, item);
      this.editedItem = this.editModelFn
        ? this.editModelFn(editModel)
        : editModel;
    },
    async onAddAsync() {
      await this.submitAction(this.addAction || `add`, this.skipAddAction);
    },
    async onEditAsync() {
      await this.submitAction(
        this.updateAction || `update`,
        this.skipUpdateAction
      );
    },
    getRequestParams() {
      let params = {};
      if (this.editedItem.id) {
        params.id = this.editedItem.id;
      }
      if (this.requestParamsFn) {
        this.requestParamsFn(params, this.editedItem);
      }
      return params;
    },
    async getRequestData() {
      return this.saveModelFn
        ? await this.saveModelFn(Object.assign({}, this.editedItem))
        : this.editedItem;
    },
    async submitAction(action, skipMainAction) {
      try {
        let params = this.getRequestParams();
        let data = await this.getRequestData();
        let result = null;
        if (!skipMainAction) {
          result = await this.$store.dispatch(
            `${this.ns}${action}${this.capitalizedEntityName}`,
            {
              params,
              data,
            }
          );
        }
        if (this.onSubmitAction) {
          await this.onSubmitAction(this.editedItem, params, data);
        }
        this.close();
        this.$emit("success", result);
      } catch (error) {
        let errMsg = this.saveError;
        if (!errMsg && error) {
          errMsg = getErrorMessage(error);
        }
        if (!errMsg && this.$te(`${this.localizationSectionName}.error`)) {
          errMsg = this.$t(`${this.localizationSectionName}.error`);
        }
        if (!errMsg) {
          errMsg = this.$t("common.error_occured");
        }
        this.setDialogError(errMsg);
      }
    },
  },
  watch: {
    editedItem: {
      deep: true,
      handler(val) {
        this.$emit("update:editedItem", val);
      },
    },
  },
};
</script>